// Initiative type configurations

export type InitiativeType =
  | 'quiz'
  | 'campaign'
  | 'competition'
  | 'giveaway'
  | 'promotion'
  | 'event'
  | 'course'
  | 'activation'
  | 'workshop'
  | 'collaboration'
  | 'role'
  | 'webinar'
  | 'officehours'
  | 'program';

export interface InitiativeTypeConfig {
  label: string;
  color: string;
  description?: string;
  mainPageUrl?: (id: number) => string;
}

export const INITIATIVE_TYPE_CONFIG: Record<InitiativeType, InitiativeTypeConfig> = {
  quiz: {
    label: 'Quiz',
    color: 'bg-purple-100 text-purple-800',
    description: 'Create an interactive quiz with custom questions and settings',
    mainPageUrl: (id: number) => `/quizzes/${id}`,
  },
  officehours: {
    label: 'Office Hours',
    color: 'bg-gray-100 text-gray-800',
    description: 'Office Hours',
  },
  program: {
    label: 'Program',
    color: 'bg-indigo-100 text-indigo-800',
    description: 'Plan + initiatives to achieve some outcome over time',
  },
  workshop: {
    label: 'Workshop',
    color: 'bg-amber-100 text-amber-800',
    description: 'Workshop - in person or online',
  },
  campaign: {
    label: 'Campaign',
    color: 'bg-blue-100 text-blue-800',
    description: 'Launch a marketing campaign for your products',
  },
  collaboration: {
    label: 'Collaboration',
    color: 'bg-cyan-100 text-cyan-800',
    description: 'Collaborate with other brands and businesses',
  },
  giveaway: {
    label: 'Giveaway',
    color: 'bg-pink-100 text-pink-800',
    description: 'A giveaway usually requiring entry',
  },
  competition: {
    label: 'Competition',
    color: 'bg-orange-100 text-orange-800',
    description: 'Run a competition to engage your audience',
  },
  promotion: {
    label: 'Promotion',
    color: 'bg-green-100 text-green-800',
    description: 'Offer special discounts and promotions',
  },
  event: {
    label: 'Event',
    color: 'bg-indigo-100 text-indigo-800',
    description: 'Organize an event for your brand or products',
  },
  course: {
    label: 'Course',
    color: 'bg-emerald-100 text-emerald-800',
    description: 'Course - online or in-person',
  },
  activation: {
    label: 'Activation',
    color: 'bg-yellow-100 text-yellow-800',
    description: 'Create a brand activation experience',
  },
  role: {
    label: 'Role',
    color: 'bg-teal-100 text-teal-800',
    description: 'Post a job hire or role opportunity',
  },
  webinar: {
    label: 'Webinar',
    color: 'bg-red-100 text-red-800',
    description: 'Host an online webinar or live session',
  },
} as const;

// Helper function to get initiative type config
export function getInitiativeTypeConfig(type: string): InitiativeTypeConfig {
  return (
    INITIATIVE_TYPE_CONFIG[type as InitiativeType] || {
      label: type || 'Initiative',
      color: 'bg-gray-100 text-gray-800',
    }
  );
}

// Helper function to get initiative URL
export function getInitiativeUrl(type: string, id: number): string {
  const config = getInitiativeTypeConfig(type);
  return config.mainPageUrl ? config.mainPageUrl(id) : `/initiatives/${id}`;
}

// Initiative categories
export const INITIATIVE_CATEGORY_OPTIONS = [
  { key: 'trade', label: 'Trade' },
  { key: 'consumer', label: 'Consumer' },
  { key: 'workplace', label: 'Workplace' },
] as const;

// Initiative activities
export const INITIATIVE_TAG_OPTIONS: { key: string; label: string; scopes?: string[] }[] = [
  { key: 'product_sampling', label: 'Product Sampling', scopes: ['activity'] },
  { key: 'product_demo', label: 'Product Demonstration', scopes: ['activity'] },
  { key: 'pop_up', label: 'Pop-up Stall / Booth' },
  { key: 'promotional_giveaway', label: 'Promotional Giveaway' },
  { key: 'workshop', label: 'Workshop', scopes: ['activity'] },
  { key: 'event_sponsorship', label: 'Event Sponsorship' },
  { key: 'brand_display', label: 'Brand Display / Installation' },
  { key: 'cooking_demo', label: 'Cooking Demo', scopes: ['activity'] },
  { key: 'product_launch', label: 'Product Launch' },
] as const;

// Activation role options
export const ACTIVATION_ROLE_OPTIONS = [
  { key: 'host', label: 'Host Activations' },
  { key: 'run', label: 'Run Activations' },
  { key: 'sponsor', label: 'Sponsor Activations' },
  { key: 'collaborate', label: 'Co-create Activations' },
  { key: 'supply_products', label: 'Provide Products' },
  { key: 'support_services', label: 'Support Services / Logistics' },
] as const;

// Activation environment options
export const ACTIVATION_ENVIRONMENT_OPTIONS = [
  { key: 'retail_store', label: 'Retail Store' },
  { key: 'workplace', label: 'Workplace / Office' },
  { key: 'public_venue', label: 'Public Venue' },
  { key: 'event_festival', label: 'Event / Festival' },
  { key: 'hospitality_venue', label: 'Hospitality Venue' },
  { key: 'tourism_destination', label: 'Tourism Destination' },
  { key: 'university_campus', label: 'University / Campus' },
  { key: 'gym_sports', label: 'Gym / Sports Venue' },
  { key: 'transport_hub', label: 'Transport Hub' },
] as const;

// Activation audience profile options
export const ACTIVATION_AUDIENCE_OPTIONS = [
  { key: 'staff', label: 'Staff / Employees' },
  { key: 'students', label: 'Students' },
  { key: 'families', label: 'Families' },
  { key: 'tourists', label: 'Tourists' },
  { key: 'office_professionals', label: 'Office Professionals' },
  { key: 'local_residents', label: 'Local Residents' },
  { key: 'event_attendees', label: 'Event Attendees' },
  { key: 'members_only', label: 'Members Only' },
] as const;

// Activation capabilities (facilities/equipment)
export const ACTIVATION_CAPABILITY_OPTIONS = [
  { key: 'indoor_space', label: 'Indoor Space' },
  { key: 'outdoor_space', label: 'Outdoor Space' },
  { key: 'table_space', label: 'Table / Booth Space' },
  { key: 'power', label: 'Power Access' },
  { key: 'water', label: 'Water Access' },
  { key: 'kitchen', label: 'Kitchen Access' },
  { key: 'storage', label: 'Storage Space' },
  { key: 'fridge_freezer', label: 'Fridge / Freezer' },
  { key: 'av_equipment', label: 'AV Equipment' },
  { key: 'wifi', label: 'WiFi' },
] as const;

// Activation terms (commercial model)
export const ACTIVATION_TERMS_OPTIONS = [
  { key: 'free', label: 'Free' },
  { key: 'paid', label: 'Paid' },
  { key: 'case_by_case', label: 'Case by Case' },
] as const;

// Activation requirements
export const ACTIVATION_REQUIREMENT_OPTIONS = [
  { key: 'insurance_required', label: 'Insurance Required' },
  { key: 'food_safety', label: 'Food Safety Compliance' },
  { key: 'pre_approval', label: 'Pre-approval Required' },
  { key: 'must_provide_staff', label: 'Must Provide Staff' },
  { key: 'must_supply_equipment', label: 'Must Supply Equipment' },
  { key: 'follow_venue_guidelines', label: 'Must Follow Venue Guidelines' },
] as const;

// Activation availability
export const ACTIVATION_AVAILABILITY_OPTIONS = [
  { key: 'anytime', label: 'Anytime' },
  { key: 'weekdays', label: 'Weekdays' },
  { key: 'weekends', label: 'Weekends' },
  { key: 'specific_events', label: 'Specific Events' },
  { key: 'seasonal', label: 'Seasonal' },
  { key: 'on_request', label: 'On Request' },
] as const;

// Activation promotion support
export const ACTIVATION_PROMOTION_OPTIONS = [
  { key: 'social_media', label: 'Social Media Promotion' },
  { key: 'email_newsletter', label: 'Email Newsletter' },
  { key: 'in_store_signage', label: 'In-store Signage' },
  { key: 'website_listing', label: 'Website Listing' },
  { key: 'event_promotion', label: 'Event Promotion' },
] as const;

// Brand category preferences for activations
export const PRODUCT_CATEGORY_OPTIONS = [
  { key: 'food', label: 'Food' },
  { key: 'beverage', label: 'Beverage' },
  { key: 'alcohol', label: 'Alcohol' },
  { key: 'health_wellness', label: 'Health & Wellness' },
  { key: 'lifestyle', label: 'Lifestyle' },
  { key: 'beauty', label: 'Beauty' },
  { key: 'pet', label: 'Pet' },
] as const;

// Initiative occasion options
export const INITIATIVE_OCCASION_OPTIONS = [
  { key: 'kids_party', label: 'Kids Party' },
  { key: 'orientation_week', label: 'Orientation Week' },
  { key: 'corporate_event', label: 'Corporate Event' },
  { key: 'team_building', label: 'Team Building' },
  { key: 'product_launch', label: 'Product Launch' },
  { key: 'networking', label: 'Networking' },
  { key: 'conference', label: 'Conference' },
  { key: 'trade_show', label: 'Trade Show' },
  { key: 'festival', label: 'Festival' },
  { key: 'market_day', label: 'Market Day' },
  { key: 'open_day', label: 'Open Day' },
  { key: 'fundraiser', label: 'Fundraiser' },
  { key: 'community_event', label: 'Community Event' },
  { key: 'sports_event', label: 'Sports Event' },
  { key: 'wellness_event', label: 'Wellness Event' },
  { key: 'seasonal_celebration', label: 'Seasonal Celebration' },
  { key: 'pop_up', label: 'Pop-up' },
] as const;

// Demographics age group options
export const DEMOGRAPHICS_AGE_GROUP_OPTIONS = [
  { key: '0-10', label: '0 - 10 years' },
  { key: '11-17', label: '11 - 17 years' },
  { key: '18-24', label: '18 - 25 years' },
  { key: '25-45', label: '25 - 45 years' },
  { key: '45-60', label: '45 - 60 years' },
  { key: '60-80', label: '60 - 80 years' },
  { key: '80+', label: '80+ years' },
] as const;

// Demographics audience type options
export const DEMOGRAPHICS_GENDER_MIX_OPTIONS = [
  { key: '', label: 'Not Specified' },
  { key: 'mostly_female', label: 'Mostly female' },
  { key: 'mostly_male', label: 'Mostly male' },
  { key: 'balanced', label: 'Balanced' },
  { key: 'diverse', label: 'Diverse' },
] as const;

// Initiative statuses that are publicly visible (no auth/permission required)
export const INITIATIVE_PUBLIC_STATUSES = ['active', 'gauging'] as const;

/** Check if an initiative status is publicly visible */
export function isPublicInitiativeStatus(status: string): boolean {
  return (INITIATIVE_PUBLIC_STATUSES as readonly string[]).includes(status);
}
